import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useInsuranceTypeConfiguration } from "utils/hooks/useInsuranceTypeConfiguration";
import insuranceTypeNames from "constants/insuranceTypeNames";
import { durationCondition } from "constants/rtiBehaviour";
import GeneralInsuranceDataCard from "./GeneralInsuranceDataCard";
import { between, fromModel, required, shape, stopOnFirstFailure, when } from "@totalsoft/pure-validations";
import { useValidation } from "@totalsoft/pure-validations-react";
import { get, useChangeTrackingLens } from "@totalsoft/change-tracking-react";
import { isDirty } from "@totalsoft/change-tracking";
import { makeReference, useApolloClient, useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { GENERAL_INSURANCE_DATA_SAVED } from "../subscriptions";
import { useGlobalSubscription } from "providers/GlobalCacheManager";
import { GENERAL_INSURANCE_DATA_SAVED_EVENT } from "../eventTypes";
import { SAVE_GENERAL_INSURANCE_DATA } from "../mutations";
import { useToast, useActivityIndicatorContext } from "utils/hooks";
import { useServerErrorHandling } from "utils/hooks/useServerErrorHandling";
import useInsuranceParams from "../useInsuranceParams";
import { commonGeneralDataVar } from "../variables";

const validator = fromModel(generalInsuranceData =>
  shape({
    financedPeriodRca:
      [required |> when(generalInsuranceData.financedInsuranceRca), between(1, generalInsuranceData.duration)]
      |> stopOnFirstFailure,
    financedPeriodCasco:
      [required |> when(generalInsuranceData.financedInsuranceCasco), between(1, generalInsuranceData.duration)]
      |> stopOnFirstFailure,
    financedPeriodRti:
      [
        required |> when(generalInsuranceData.financedInsuranceRti),
        between(
          1,
          generalInsuranceData.duration > durationCondition.contractMaxDuration
            ? durationCondition.contractMaxDuration
            : generalInsuranceData.duration
        )
      ] |> stopOnFirstFailure,
    ownCasco: required,
    ownRca: required
  })
);

const allowFinancing = (leasingSimulation, insuranceTypeConfiguration) => {
  if (!leasingSimulation?.allowInsuranceFinancing) return false;

  if (insuranceTypeConfiguration?.insuranceTypeName === insuranceTypeNames.RTI) {
    if (
      !leasingSimulation?.documentDetails
        ?.map(x => x?.insuranceSimulations?.find(x => x.insuranceTypeName === insuranceTypeNames.CASCO))
        ?.find(x => x?.hasRtiOption)
    )
      return false;
    return true;
  }

  if (
    leasingSimulation?.documentDetails?.map(x =>
      x.insuranceSimulations?.find(x => x.insuranceTypeCode === insuranceTypeConfiguration?.propertyCode)
    )?.length === 0 ||
    !leasingSimulation?.documentDetails
      ?.map(x => x?.insuranceSimulations?.find(x => x.insuranceTypeCode === insuranceTypeConfiguration?.propertyCode))
      ?.find(x => x?.selectedInsuranceQuotationId)
  )
    return false;
  return true;
};

const GeneralInsuranceDataContainer = ({ leasingOfferData, refetch }) => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const { onBeginCall, onEndCall } = useActivityIndicatorContext();
  const addToast = useToast();

  const insuranceTypeConfig = useInsuranceTypeConfiguration();
  const { offerId, offerSiteId } = useInsuranceParams();
  const [validation, validate] = useValidation(validator);

  const [saving, setSaving] = useState(false);
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens();

  // #region Memos

  const insuranceData = useMemo(
    () => ({
      ...leasingOfferData?.leasingOffer?.leasingSimulation?.insuranceData,
      duration: leasingOfferData?.leasingOffer?.leasingSimulation?.duration
    }),
    [leasingOfferData]
  );
  const leasingSimulation = useMemo(() => leasingOfferData?.leasingOffer?.leasingSimulation, [leasingOfferData]);
  const localInsuranceSimulations = useMemo(
    () => leasingSimulation?.documentDetails?.map(detail => detail.insuranceSimulations)?.flat(),
    [leasingSimulation]
  );
  const isLensDirty = useMemo(() => isDirty(dirtyInfo), [dirtyInfo]);

  // #endregion

  useEffect(() => {
    if (saving || !insuranceData || !leasingSimulation || isLensDirty) return;

    resetLens({
      ...insuranceData,
      allowRcaFinancing: allowFinancing(leasingSimulation, insuranceTypeConfig.rca),
      allowCascoFinancing: allowFinancing(leasingSimulation, insuranceTypeConfig.casco),
      allowRtiFinancing: allowFinancing(leasingSimulation, insuranceTypeConfig.rti)
    });
    commonGeneralDataVar({
      ownRca: insuranceData.ownRca,
      ownCasco: insuranceData.ownCasco,
      ownCascoReadOnly: insuranceData.ownCascoReadOnly
    });
  }, [resetLens, saving, insuranceData, leasingSimulation, insuranceTypeConfig, isLensDirty]);

  // #region Subscriptions

  useGlobalSubscription(GENERAL_INSURANCE_DATA_SAVED, {
    variables: { offerId, offerSiteId },
    onSubscriptionData: ({
      subscriptionData: {
        data: { generalInsuranceDataSaved }
      }
    }) => {
      resetLens(prev => ({ ...prev, ...generalInsuranceDataSaved.insuranceData }));

      localInsuranceSimulations.forEach(({ id }) => {
        client.cache.evict({
          id: client.cache.identify(makeReference("ROOT_QUERY")),
          fieldName: `quotations({"insuranceSimulationId":${id}})`
        });
        const insuranceSimulationCacheId = client.cache.identify({ __typename: "InsuranceSimulation", id });
        client.cache.evict({ id: insuranceSimulationCacheId, fieldName: "financedInsurance" });
        client.cache.evict({ id: insuranceSimulationCacheId, fieldName: "financedInsurancePeriod" });
        client.cache.evict({ id: insuranceSimulationCacheId, fieldName: "periodicity" });
      });
      client.cache.gc();

      if (generalInsuranceDataSaved.isManual) refetch();
      addToast(t("Notifications." + GENERAL_INSURANCE_DATA_SAVED_EVENT), "info");
      onEndCall();
      setSaving(false);
    }
  });

  // #endregion

  // #region Save

  const { storeKey } = useServerErrorHandling({
    effect: _ => {
      setSaving(false);
      onEndCall();
    }
  });

  const [saveGeneralInsuranceData] = useMutation(SAVE_GENERAL_INSURANCE_DATA, {
    context: {
      headers: { offerId, offerSiteId }
    },
    onError: err => {
      addToast(err.message, "error");
      onEndCall();
      setSaving(false);
    },
    onCompleted: data => {
      const { correlationId } = data.saveGeneralInsuranceData;
      storeKey({ correlationId });
    }
  });

  const handleSave = useCallback(() => {
    if (!validate(lens |> get)) {
      return;
    }

    const insuranceSimulationIds = localInsuranceSimulations?.map(x => x.id);
    onBeginCall();
    setSaving(true);

    saveGeneralInsuranceData({
      variables: {
        input: {
          offerId,
          offerSiteId,
          insuranceSimulationIds: insuranceSimulationIds,
          leasingDocumentInsuranceData: {
            ownRca: lens.ownRca |> get,
            ownCasco: lens.ownCasco |> get,
            ownRcaReadOnly: lens.ownRcaReadOnly |> get,
            ownCascoReadOnly: lens.ownCascoReadOnly |> get,
            financedInsuranceRca: lens.financedInsuranceRca |> get,
            financedPeriodRca: lens.financedPeriodRca |> get,
            financedInsuranceCasco: lens.financedInsuranceCasco |> get,
            financedPeriodCasco: lens.financedPeriodCasco |> get,
            financedInsuranceRti: lens.financedInsuranceRti |> get,
            financedPeriodRti: lens.financedPeriodRti |> get
          }
        }
      }
    });

    commonGeneralDataVar({
      ownRca: lens.ownRca |> get,
      ownCasco: lens.ownCasco |> get,
      ownCascoReadOnly: lens.ownCascoReadOnly |> get
    });
  }, [offerId, offerSiteId, lens, localInsuranceSimulations, saveGeneralInsuranceData, onBeginCall, validate]);

  // #endregion

  return (
    <GeneralInsuranceDataCard
      lens={lens}
      onSave={handleSave}
      dirtyInfo={dirtyInfo}
      saving={saving}
      validation={validation}
    />
  );
};

GeneralInsuranceDataContainer.propTypes = {
  leasingOfferData: PropTypes.object,
  refetch: PropTypes.func.isRequired
};

export default GeneralInsuranceDataContainer;
